import { Table, Thead, Tbody, Tr, Th, Td } from '@components/core-ui/Table/Table';
import DiagonalArrow from '@components/icons/DiagonalArrow.svg';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AdminJobLlmCallDto } from 'app/API';
import { useMemo } from 'react';
import { formatLlmDuration } from './helpers/formatLlmDuration';
import { formatTokenCount } from './helpers/formatTokenCount';
import { getLangfuseSessionUrl } from './helpers/getLangfuseSessionUrl';

type TProps = {
  onCancel: () => void;
  jobId?: string;
  llmCalls?: AdminJobLlmCallDto[];
};

const formatRequestTime = (value?: string | null) => {
  return value ? getAmericanTime(value) : '-';
};

const getRequestTimeMs = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const AdminLlmCallsModal = ({ jobId, llmCalls }: TProps) => {
  const sortedLlmCalls = useMemo(() => {
    return [...(llmCalls ?? [])].sort((firstCall, secondCall) => {
      return getRequestTimeMs(secondCall.requestedAt) - getRequestTimeMs(firstCall.requestedAt);
    });
  }, [llmCalls]);
  const groupedLlmCalls = useMemo(() => {
    const groups: { pipelineAttemptId: string; calls: typeof sortedLlmCalls }[] = [];

    for (const call of sortedLlmCalls) {
      const pipelineAttemptId = call.pipelineAttemptId ?? '-';
      const lastGroup = groups.at(-1);

      if (lastGroup?.pipelineAttemptId === pipelineAttemptId) {
        lastGroup.calls.push(call);
      } else {
        groups.push({ pipelineAttemptId, calls: [call] });
      }
    }

    return groups;
  }, [sortedLlmCalls]);
  const langfuseSessionUrl = getLangfuseSessionUrl(jobId);

  return (
    <ModalWrapper width="1200px">
      <ModalTitle>LLM Calls</ModalTitle>

      <div className="break-words text-base font-normal leading-[150%] text-[var(--color-text)]">
        {langfuseSessionUrl && (
          <a
            href={langfuseSessionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-primary)] underline-offset-2 hover:underline [&_svg]:h-4 [&_svg]:w-4"
          >
            <DiagonalArrow />
            View Langfuse trace
          </a>
        )}
        {!sortedLlmCalls.length ? (
          <div className="py-4 text-sm text-[var(--color-text-muted)]">No LLM calls recorded for this job.</div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table className="w-full min-w-full max-w-full border-collapse text-base font-normal leading-[150%] text-[var(--color-text)]">
              <Thead className="relative bg-[var(--color-page)] after:absolute after:bottom-0 after:left-0 after:block after:w-full after:border-b after:border-[var(--color-border)] after:content-['']">
                <Tr className="h-10 border-0 text-left text-xs font-semibold uppercase text-[var(--color-text-muted)]">
                  <Th className="h-10 w-[18%] max-w-[350px] whitespace-nowrap pr-5 align-middle">Pipeline Attempt</Th>
                  <Th className="h-10 w-[18%] max-w-[350px] whitespace-nowrap pr-5 align-middle">LLM Feature</Th>
                  <Th className="h-10 w-[16%] max-w-[350px] whitespace-nowrap pr-5 align-middle">Model</Th>
                  <Th className="h-10 w-[11%] max-w-[350px] whitespace-nowrap pr-5 align-middle">Requested At</Th>
                  <Th className="h-10 w-[11%] max-w-[350px] whitespace-nowrap pr-5 align-middle">Completed At</Th>
                  <Th className="h-10 w-[8%] max-w-[350px] whitespace-nowrap pr-5 align-middle">Duration</Th>
                  <Th className="h-10 w-[6%] max-w-[350px] whitespace-nowrap pr-5 align-middle">Prompt Tokens</Th>
                  <Th className="h-10 w-[6%] max-w-[350px] whitespace-nowrap pr-5 align-middle">Completion Tokens</Th>
                  <Th className="h-10 w-[6%] max-w-[350px] whitespace-nowrap align-middle">Total Tokens</Th>
                </Tr>
              </Thead>
              <Tbody>
                {groupedLlmCalls.flatMap((group, groupIndex) =>
                  group.calls.map((call, index) => (
                    <Tr
                      key={`${call.source ?? call.promptSlug ?? call.model ?? 'llm-call'}-${groupIndex}-${index}`}
                      className={`border-t border-[var(--color-border)] ${
                        groupIndex % 2 === 0 ? 'bg-[var(--color-page)]' : 'bg-[var(--color-surface-muted)]'
                      }`}
                    >
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div
                          className="overflow-hidden text-ellipsis whitespace-nowrap"
                          title={call.pipelineAttemptId ?? '-'}
                        >
                          {call.pipelineAttemptId ?? '-'}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap" title={call.promptSlug ?? '-'}>
                          {call.promptSlug ?? '-'}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div
                          className="overflow-hidden text-ellipsis whitespace-nowrap"
                          title={call.model ?? call.outputModel ?? call.inputModel ?? '-'}
                        >
                          {call.model ?? call.outputModel ?? call.inputModel ?? '-'}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div
                          className="overflow-hidden text-ellipsis whitespace-nowrap"
                          title={call.requestedAt ?? undefined}
                        >
                          {formatRequestTime(call.requestedAt)}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div
                          className="overflow-hidden text-ellipsis whitespace-nowrap"
                          title={call.completedAt ?? undefined}
                        >
                          {formatRequestTime(call.completedAt)}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {formatLlmDuration(call.durationMs)}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {formatTokenCount(call.promptTokens)}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 pr-5 align-middle">
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {formatTokenCount(call.completionTokens)}
                        </div>
                      </Td>
                      <Td className="max-w-[350px] whitespace-nowrap py-3 align-middle">
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {formatTokenCount(call.totalTokens)}
                        </div>
                      </Td>
                    </Tr>
                  )),
                )}
              </Tbody>
            </Table>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};
